<template>
  <inputComponent
    ref="inputRef"
    v-model="modelValue"
    :helper-text="displayHelperText"
    :state-error="showValidation && isInvalid"
    @blur="validate"
  >
    <template v-if="required" #label="{ label }">
      {{ label }}<span class="text-danger">*</span>
    </template>
    <!-- Pass any slot content to inputComponent slots of the same name -->
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </inputComponent>
</template>

<script setup>
import {
  ref,
  useTemplateRef,
  computed,
  watch,
  inject,
  onMounted,
  onUnmounted,
} from "vue"

const props = defineProps({
  inputComponent: {
    type: Object,
    required: true,
  },
  validator: {
    type: Function,
    default: () => ({ valid: true }),
  },
  helperText: {
    type: String,
    default: "",
  },
  required: Boolean,
  validateOnChange: Boolean,
})

const modelValue = defineModel({ type: [String, Number, Boolean, Object] })

const inputRef = useTemplateRef("inputRef")
const showValidation = ref(false)
const validationMessage = ref("")
const isInvalid = ref(false)

/**
 * Register with ascendant ValidatedFormComponent if available
 */
const attachToForm = inject("attachToForm", null)
const detachFromForm = inject("detachFromForm", null)

onMounted(() => {
  if (attachToForm) {
    attachToForm({ validate })
  }
})

onUnmounted(() => {
  if (detachFromForm) {
    detachFromForm({ validate })
  }
})

function validate(scrollIfFailed = false) {
  showValidation.value = true
  const { valid, message } = props.validator(modelValue.value)
  isInvalid.value = !valid
  validationMessage.value = message
  if (scrollIfFailed && !valid) {
    inputRef.value?.$el.scrollIntoView({ behavior: "smooth" })
  }
  return valid
}

const displayHelperText = computed(() => {
  if (showValidation.value && isInvalid.value) {
    return validationMessage.value
  }
  return props.helperText
})

watch(modelValue, () => {
  if (showValidation.value || props.validateOnChange) {
    validate()
  }
})

defineExpose({
  validate,
  inputRef,
})
</script>
